import * as XLSX from 'xlsx';
import { csvJSON } from './CsvToJson';

export const COMPANY_ENTITY_RELATIONSHIP_TYPES = [
  'parent_company',
  'subsidiary',
  'division',
  'franchise',
  'branch_office',
] as const;

export const PERSON_ENTITY_RELATIONSHIP_TYPES = [
  'director',
  'chairperson',
  'ceo',
  'cfo',
  'coo',
  'cto',
  'president',
  'employee',
  'founder',
  'partner',
  'shareholder',
  'board_member',
] as const;

export const ENTITY_RELATIONSHIP_TYPES = [
  ...COMPANY_ENTITY_RELATIONSHIP_TYPES,
  ...PERSON_ENTITY_RELATIONSHIP_TYPES,
] as const;

export const RELATIONSHIP_BULK_UPLOAD_MAX_ROWS = 10000;

export const RELATIONSHIP_SOURCE_BUSINESS_HEADER =
  'source_business_registration_number_or_ein';
export const RELATIONSHIP_TARGET_BUSINESS_HEADER =
  'target_business_registration_number_or_ein';
export const RELATIONSHIP_SOURCE_LINK_HEADER = 'source_link_id';
export const RELATIONSHIP_SOURCE_NAME_HEADER = 'source_name';
export const RELATIONSHIP_TARGET_LINK_HEADER = 'target_link_id';
export const RELATIONSHIP_TARGET_NAME_HEADER = 'target_name';
export const RELATIONSHIP_SOURCE_PERSON_HEADER = 'source_person_id';
export const RELATIONSHIP_TARGET_PERSON_HEADER = 'target_person_id';
export const RELATIONSHIP_TYPE_HEADER = 'relationship_type';

const LEGACY_RELATIONSHIP_SOURCE_BUSINESS_HEADER =
  'source_business_registration_number';
const LEGACY_RELATIONSHIP_TARGET_BUSINESS_HEADER =
  'target_business_registration_number';

const RELATIONSHIP_ACRONYM_LABELS: Record<string, string> = {
  ceo: 'CEO',
  cfo: 'CFO',
  coo: 'COO',
  cto: 'CTO',
};

const COMPANY_RELATIONSHIP_TYPES = new Set<string>(
  COMPANY_ENTITY_RELATIONSHIP_TYPES as unknown as string[],
);

const PERSON_RELATIONSHIP_TYPES = new Set<string>(
  PERSON_ENTITY_RELATIONSHIP_TYPES as unknown as string[],
);

export const formatEntityRelationshipTypeLabel = (value: string) =>
  value
    .split('_')
    .map((word) => {
      const normalized = word.toLowerCase();
      return (
        RELATIONSHIP_ACRONYM_LABELS[normalized] ||
        normalized.charAt(0).toUpperCase() + normalized.slice(1)
      );
    })
    .join(' ');

export interface ParsedRelationshipRow {
  source_link_id?: string;
  source_business_registration_number?: string;
  source_person_id?: string;
  target_link_id?: string;
  target_business_registration_number?: string;
  target_person_id?: string;
  relationship_type: string;
}

const normalizeCell = (value: unknown) => String(value ?? '').trim();

const getRowValue = (row: Record<string, unknown>, ...keys: string[]) => {
  for (const key of keys) {
    const value = normalizeCell(row[key]);
    if (value) {
      return value;
    }
  }

  return '';
};

export const getRelationshipCsvMappingSummary = (type: string) => {
  if (type === 'parent_company') {
    return 'Source company -> target parent company';
  }

  if (COMPANY_RELATIONSHIP_TYPES.has(type)) {
    return 'Source company -> target company';
  }

  if (PERSON_RELATIONSHIP_TYPES.has(type)) {
    return 'Source company -> target person';
  }

  return 'Source entity -> target entity';
};

export const getRelationshipCsvMeaning = (type: string) => {
  const label = formatEntityRelationshipTypeLabel(type);

  if (type === 'parent_company') {
    return 'Target company is the parent of the source company';
  }

  if (COMPANY_RELATIONSHIP_TYPES.has(type)) {
    return `Target company is the ${label.toLowerCase()} of the source company`;
  }

  if (PERSON_RELATIONSHIP_TYPES.has(type)) {
    return `Target person is the ${label.toLowerCase()} of the source company`;
  }

  return '';
};

const validateRelationshipSide = (
  linkIdentifier: string,
  businessIdentifier: string,
  personIdentifier: string,
  sideLabel: 'source' | 'target',
  lineNum: number,
) => {
  if (!linkIdentifier && !businessIdentifier && !personIdentifier) {
    const linkHeader =
      sideLabel === 'source'
        ? RELATIONSHIP_SOURCE_LINK_HEADER
        : RELATIONSHIP_TARGET_LINK_HEADER;
    const businessHeader =
      sideLabel === 'source'
        ? RELATIONSHIP_SOURCE_BUSINESS_HEADER
        : RELATIONSHIP_TARGET_BUSINESS_HEADER;
    const personHeader =
      sideLabel === 'source'
        ? RELATIONSHIP_SOURCE_PERSON_HEADER
        : RELATIONSHIP_TARGET_PERSON_HEADER;
    return `Row ${lineNum}: provide ${linkHeader}, ${businessHeader}, or ${personHeader}`;
  }

  if (
    [linkIdentifier, businessIdentifier, personIdentifier].filter(Boolean)
      .length > 1
  ) {
    return `Row ${lineNum}: provide only one ${sideLabel} identifier`;
  }

  return null;
};

const validateRelationshipTypeMapping = (
  type: string,
  sourceLink: string,
  sourceBusiness: string,
  sourcePerson: string,
  targetLink: string,
  targetBusiness: string,
  targetPerson: string,
  lineNum: number,
) => {
  if (sourceLink || targetLink) {
    return null;
  }

  if (COMPANY_RELATIONSHIP_TYPES.has(type)) {
    if (!sourceBusiness || sourcePerson || !targetBusiness || targetPerson) {
      const detail =
        type === 'parent_company'
          ? `${RELATIONSHIP_SOURCE_BUSINESS_HEADER} (child company) -> ${RELATIONSHIP_TARGET_BUSINESS_HEADER} (parent company)`
          : `${RELATIONSHIP_SOURCE_BUSINESS_HEADER} -> ${RELATIONSHIP_TARGET_BUSINESS_HEADER}`;
      return `Row ${lineNum}: ${type} expects ${detail}`;
    }
    return null;
  }

  if (PERSON_RELATIONSHIP_TYPES.has(type)) {
    if (!sourceBusiness || sourcePerson || targetBusiness || !targetPerson) {
      return `Row ${lineNum}: ${type} expects ${RELATIONSHIP_SOURCE_BUSINESS_HEADER} -> ${RELATIONSHIP_TARGET_PERSON_HEADER}`;
    }
  }

  return null;
};

export const downloadRelationshipCSVTemplate = (
  filename = 'relationship_template.csv',
) => {
  const workbook = XLSX.utils.book_new();

  const relationshipRows: Record<string, string>[] = [];

  const relationshipTypeRows = ENTITY_RELATIONSHIP_TYPES.map((type) => ({
    relationship_type: type,
    label: formatEntityRelationshipTypeLabel(type),
    category: COMPANY_RELATIONSHIP_TYPES.has(type) ? 'Company' : 'Person',
    mapping: getRelationshipCsvMappingSummary(type),
    meaning: getRelationshipCsvMeaning(type),
  }));

  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(relationshipRows, {
      header: [
        RELATIONSHIP_SOURCE_LINK_HEADER,
        RELATIONSHIP_SOURCE_NAME_HEADER,
        RELATIONSHIP_SOURCE_BUSINESS_HEADER,
        RELATIONSHIP_SOURCE_PERSON_HEADER,
        RELATIONSHIP_TARGET_LINK_HEADER,
        RELATIONSHIP_TARGET_NAME_HEADER,
        RELATIONSHIP_TARGET_BUSINESS_HEADER,
        RELATIONSHIP_TARGET_PERSON_HEADER,
        RELATIONSHIP_TYPE_HEADER,
      ],
    }),
    'Relationships',
  );
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(relationshipTypeRows, {
      header: ['relationship_type', 'label', 'category', 'mapping', 'meaning'],
    }),
    'Valid Relationship Types',
  );

  if (filename.endsWith('.csv')) {
    const csv = XLSX.utils.sheet_to_csv(workbook.Sheets.Relationships);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return;
  }

  XLSX.writeFile(workbook, filename);
};

export const parseRelationshipCSV = (
  file: File,
): Promise<{ rows: ParsedRelationshipRow[]; errors: string[] }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const rawResult = e.target?.result;
        const jsonRows = file.name.endsWith('.csv')
          ? csvJSON(rawResult as string)
          : (() => {
              const workbook = XLSX.read(
                new Uint8Array(rawResult as ArrayBuffer),
                {
                  type: 'array',
                },
              );
              const sheetName = workbook.SheetNames.includes('Relationships')
                ? 'Relationships'
                : workbook.SheetNames[0];
              const sheet = workbook.Sheets[sheetName];
              return XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
                defval: '',
              });
            })();

        const validTypes = new Set<string>(
          ENTITY_RELATIONSHIP_TYPES as unknown as string[],
        );
        const rows: ParsedRelationshipRow[] = [];
        const errors: string[] = [];

        jsonRows.forEach((row, idx) => {
          const lineNum = idx + 2;
          const sourceLink = getRowValue(row, RELATIONSHIP_SOURCE_LINK_HEADER);
          const sourceBusiness = getRowValue(
            row,
            RELATIONSHIP_SOURCE_BUSINESS_HEADER,
            LEGACY_RELATIONSHIP_SOURCE_BUSINESS_HEADER,
          );
          const sourcePerson = getRowValue(
            row,
            RELATIONSHIP_SOURCE_PERSON_HEADER,
          );
          const targetLink = getRowValue(row, RELATIONSHIP_TARGET_LINK_HEADER);
          const targetBusiness = getRowValue(
            row,
            RELATIONSHIP_TARGET_BUSINESS_HEADER,
            LEGACY_RELATIONSHIP_TARGET_BUSINESS_HEADER,
          );
          const targetPerson = getRowValue(
            row,
            RELATIONSHIP_TARGET_PERSON_HEADER,
          );
          const type = getRowValue(row, RELATIONSHIP_TYPE_HEADER).toLowerCase();

          if (
            !sourceLink &&
            !sourceBusiness &&
            !sourcePerson &&
            !targetLink &&
            !targetBusiness &&
            !targetPerson &&
            !type
          ) {
            return;
          }

          const sourceError = validateRelationshipSide(
            sourceLink,
            sourceBusiness,
            sourcePerson,
            'source',
            lineNum,
          );
          if (sourceError) {
            errors.push(sourceError);
            return;
          }

          const targetError = validateRelationshipSide(
            targetLink,
            targetBusiness,
            targetPerson,
            'target',
            lineNum,
          );
          if (targetError) {
            errors.push(targetError);
            return;
          }

          if (!type) {
            errors.push(`Row ${lineNum}: missing relationship_type`);
            return;
          }

          if (!validTypes.has(type)) {
            errors.push(`Row ${lineNum}: invalid relationship_type "${type}"`);
            return;
          }

          const mappingError = validateRelationshipTypeMapping(
            type,
            sourceLink,
            sourceBusiness,
            sourcePerson,
            targetLink,
            targetBusiness,
            targetPerson,
            lineNum,
          );
          if (mappingError) {
            errors.push(mappingError);
            return;
          }

          const sourceIdentifier = sourceLink || sourceBusiness || sourcePerson;
          const targetIdentifier = targetLink || targetBusiness || targetPerson;
          if (
            sourceIdentifier &&
            targetIdentifier &&
            sourceIdentifier === targetIdentifier
          ) {
            errors.push(
              `Row ${lineNum}: source and target identifiers are the same`,
            );
            return;
          }

          rows.push({
            source_link_id: sourceLink || undefined,
            source_business_registration_number: sourceBusiness || undefined,
            source_person_id: sourcePerson || undefined,
            target_link_id: targetLink || undefined,
            target_business_registration_number: targetBusiness || undefined,
            target_person_id: targetPerson || undefined,
            relationship_type: type,
          });
        });

        if (rows.length > RELATIONSHIP_BULK_UPLOAD_MAX_ROWS) {
          errors.unshift(
            `This file contains ${rows.length.toLocaleString()} valid relationships. You can upload up to ${RELATIONSHIP_BULK_UPLOAD_MAX_ROWS.toLocaleString()} relationships at a time. Split the file into smaller batches.`,
          );
          resolve({ rows: [], errors: errors });
          return;
        }

        resolve({ rows: rows, errors: errors });
      } catch (_err) {
        reject(
          new Error(
            'Failed to parse the relationship file. Ensure it is a valid CSV or XLSX.',
          ),
        );
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    if (file.name.endsWith('.csv')) {
      reader.readAsText(file);
      return;
    }
    reader.readAsArrayBuffer(file);
  });
};



